/**
 * FamilyTreeSampleTemplates — inspiration gallery of 25 biography-style family
 * tree templates cropped to 1:1. Shows 4 tiles per slide (2×2 grid), auto-swaps
 * every 5s, dark navy backdrop (never white — user request 2026-02-11).
 *
 * Position on landing: BETWEEN "What Will Happen" and "Why Families Choose Us".
 * Admin CMS: uploads with position="family_tree_sample" replace the bundled set.
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { api } from "@/lib/api";

const ASSET_V = "20260911h";
const DEFAULT_SLIDES = Array.from({ length: 25 }, (_, i) => {
    const n = String(i + 1).padStart(2, "0");
    return {
        id: `fts-${i + 1}`,
        image_url: `/tree-templates/${n}.webp?v=${ASSET_V}`,
        full_url: `/tree-templates/${n}-full.webp?v=${ASSET_V}`,
        alt: `Family tree sample template ${i + 1}`,
    };
});

const AUTO_MS = 5000;
const PER_SLIDE = 4;

const chunk = (arr, n) => {
    const out = [];
    for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
    return out;
};

export function FamilyTreeSampleTemplates() {
    const [slides, setSlides] = useState(DEFAULT_SLIDES);
    const [pageIdx, setPageIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const [lightbox, setLightbox] = useState(null); // { src, alt, idx }

    useEffect(() => {
        let cancel = false;
        api.get("/public/family-tree-samples")
            .then(({ data }) => {
                if (cancel) return;
                if (Array.isArray(data?.items) && data.items.length > 0) setSlides(data.items);
            })
            .catch(() => { /* keep defaults */ });
        return () => { cancel = true; };
    }, []);

    const pages = useMemo(() => chunk(slides, PER_SLIDE), [slides]);
    const total = pages.length;

    useEffect(() => {
        if (paused || total < 2) return undefined;
        const t = setInterval(() => setPageIdx((i) => (i + 1) % total), AUTO_MS);
        return () => clearInterval(t);
    }, [paused, total]);

    const go = (n) => setPageIdx(((n % total) + total) % total);
    const activePage = pages[pageIdx] || [];

    // Lightbox handlers
    const openLightbox = (slide) => {
        const globalIdx = slides.findIndex((s) => s.id === slide.id);
        setLightbox({ src: slide.full_url || slide.image_url, alt: slide.alt, idx: globalIdx });
    };
    const closeLightbox = () => setLightbox(null);
    const lightboxStep = (dir) => {
        setLightbox((cur) => {
            if (!cur) return cur;
            const next = (cur.idx + dir + slides.length) % slides.length;
            const s = slides[next];
            return { src: s.full_url || s.image_url, alt: s.alt, idx: next };
        });
    };
    useEffect(() => {
        if (!lightbox) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowLeft") lightboxStep(-1);
            else if (e.key === "ArrowRight") lightboxStep(1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, slides.length]);

    return (
        <section
            id="family-tree-samples"
            aria-label="Family tree design template gallery"
            data-testid="family-tree-samples"
            className="relative py-8 md:py-12 bg-gradient-to-b from-[#08163A] via-[#0F2454] to-[#132B5F] overflow-hidden"
        >
            {/* soft ambient glow */}
            <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[80%] rounded-full bg-[#C9A55B]/25 blur-3xl" />
            {/* subtle bottom warm accent */}
            <div className="pointer-events-none absolute -bottom-24 right-0 h-56 w-[60%] rounded-full bg-[#F59E0B]/15 blur-3xl" />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-8 md:mb-10">
                    <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-white/8 border border-[#C9A55B]/40 backdrop-blur-sm">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />
                        <p className="text-[10.5px] sm:text-[11.5px] uppercase tracking-[0.22em] font-bold text-[#F59E0B]">
                            Sample Designs
                        </p>
                        <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />
                    </div>
                    <h2 className="mt-2 font-extrabold tracking-[-0.02em] text-white leading-[1.1]"
                        style={{ fontSize: "clamp(24px, 4vw, 38px)" }}
                    >
                        Family Tree <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F59E0B]">Sample Designs</span>
                    </h2>
                    <p className="mt-2.5 text-[13px] sm:text-[14.5px] text-white/75 max-w-xl mx-auto">
                        A glimpse of how your family biography &amp; tree can be preserved forever — crafted with care, printed for generations.
                    </p>
                </div>

                {/* Grid — 4 tiles per slide, 1:1 square. Click any tile → lightbox opens with the full portrait poster. */}
                <div className="relative rounded-2xl overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={pageIdx}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6"
                            data-testid={`fts-page-${pageIdx}`}
                        >
                            {activePage.map((s, i) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => openLightbox(s)}
                                    className="relative aspect-square rounded-2xl overflow-hidden bg-[#F5E9CE] shadow-[0_20px_50px_rgba(11,30,74,0.6),inset_0_0_0_1px_rgba(201,165,91,0.6)] hover:shadow-[0_28px_60px_rgba(245,158,11,0.4),inset_0_0_0_2px_rgba(245,158,11,0.9)] transition-all duration-300 hover:-translate-y-1 cursor-zoom-in group"
                                    data-testid={`fts-tile-${pageIdx}-${i}`}
                                    aria-label={`Open ${s.alt} full view`}
                                >
                                    <img
                                        src={s.image_url}
                                        alt={s.alt}
                                        loading={pageIdx === 0 ? "eager" : "lazy"}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                                        draggable={false}
                                    />
                                    {/* Gold frame corners */}
                                    <span aria-hidden="true" className="pointer-events-none absolute top-1.5 left-1.5 h-4 w-4 border-t-2 border-l-2 border-[#F59E0B]/80 rounded-tl-md" />
                                    <span aria-hidden="true" className="pointer-events-none absolute top-1.5 right-1.5 h-4 w-4 border-t-2 border-r-2 border-[#F59E0B]/80 rounded-tr-md" />
                                    <span aria-hidden="true" className="pointer-events-none absolute bottom-1.5 left-1.5 h-4 w-4 border-b-2 border-l-2 border-[#F59E0B]/80 rounded-bl-md" />
                                    <span aria-hidden="true" className="pointer-events-none absolute bottom-1.5 right-1.5 h-4 w-4 border-b-2 border-r-2 border-[#F59E0B]/80 rounded-br-md" />
                                    {/* Bottom vignette on hover */}
                                    <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <span className="pointer-events-none absolute bottom-2.5 left-1/2 -translate-x-1/2 text-[10.5px] font-semibold text-white bg-[#0B1E4A]/85 backdrop-blur-sm rounded-full px-2.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                        Click to view full
                                    </span>
                                </button>
                            ))}
                            {/* Pad last page so grid stays 4-wide */}
                            {activePage.length < PER_SLIDE &&
                                Array.from({ length: PER_SLIDE - activePage.length }).map((_, i) => (
                                    <div
                                        key={`pad-${i}`}
                                        aria-hidden="true"
                                        className="relative aspect-square rounded-2xl bg-white/5 ring-1 ring-white/10"
                                    />
                                ))}
                        </motion.div>
                    </AnimatePresence>

                    {/* Prev / Next arrows */}
                    {total > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={() => { setPaused(true); go(pageIdx - 1); setTimeout(() => setPaused(false), 6000); }}
                                aria-label="Previous templates"
                                data-testid="fts-prev"
                                className="absolute top-1/2 -translate-y-1/2 -left-1 sm:-left-2 h-9 w-9 rounded-full bg-white/95 text-[#0B1E4A] inline-flex items-center justify-center shadow-md hover:bg-white z-10"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => { setPaused(true); go(pageIdx + 1); setTimeout(() => setPaused(false), 6000); }}
                                aria-label="Next templates"
                                data-testid="fts-next"
                                className="absolute top-1/2 -translate-y-1/2 -right-1 sm:-right-2 h-9 w-9 rounded-full bg-white/95 text-[#0B1E4A] inline-flex items-center justify-center shadow-md hover:bg-white z-10"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </>
                    )}
                </div>

                {/* Progress + counter */}
                <div className="mt-4 flex items-center justify-center gap-3" data-testid="fts-progress">
                    <div className="text-[11px] font-semibold text-[#F59E0B] tracking-wide tabular-nums">
                        {String(pageIdx + 1).padStart(2, "0")} <span className="text-white/50">/ {String(total).padStart(2, "0")}</span>
                    </div>
                    <div className="w-40 h-1 rounded-full bg-white/15 overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]"
                            animate={{ width: `${((pageIdx + 1) / total) * 100}%` }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        />
                    </div>
                </div>
            </div>

            {/* Lightbox — full-screen modal that shows the tapped poster at natural portrait size */}
            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={closeLightbox}
                        className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center px-3 py-6"
                        data-testid="fts-lightbox"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Family tree template full view"
                    >
                        {/* Close */}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                            aria-label="Close full view"
                            data-testid="fts-lightbox-close"
                            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/95 text-[#0B1E4A] inline-flex items-center justify-center shadow-lg hover:bg-white z-[71]"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        {/* Prev */}
                        {slides.length > 1 && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); lightboxStep(-1); }}
                                aria-label="Previous template"
                                data-testid="fts-lightbox-prev"
                                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/95 text-[#0B1E4A] inline-flex items-center justify-center shadow-lg hover:bg-white z-[71]"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                        )}
                        {/* Poster image */}
                        <motion.img
                            key={lightbox.src}
                            initial={{ scale: 0.94, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            src={lightbox.src}
                            alt={lightbox.alt}
                            className="relative max-h-[92vh] max-w-[92vw] sm:max-w-[80vw] md:max-w-[60vw] lg:max-w-[440px] xl:max-w-[520px] w-auto h-auto rounded-lg shadow-2xl object-contain bg-[#F5E9CE]"
                            data-testid="fts-lightbox-img"
                        />
                        {/* Next */}
                        {slides.length > 1 && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); lightboxStep(1); }}
                                aria-label="Next template"
                                data-testid="fts-lightbox-next"
                                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/95 text-[#0B1E4A] inline-flex items-center justify-center shadow-lg hover:bg-white z-[71]"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        )}
                        {/* Counter caption */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[12px] font-semibold text-white/90 bg-black/45 rounded-full px-3 py-1" data-testid="fts-lightbox-counter">
                            {String(lightbox.idx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

export default FamilyTreeSampleTemplates;
